import React, { useState, useEffect, useCallback } from 'react';
import { Plus, PencilSimple, Key, Trash, Check, X, CaretDown, CaretUp, User, EnvelopeSimple, Phone, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { SalaryModeBadge } from '../shared/SalaryConfigSection';

const API = `${window.location.origin}/api`;

export default function EmployeesTab({
    employees,
    onAddEmployee,
    onEditEmployee,
    onResetPassword,
    onDeleteEmployee,
    onRefresh,
}) {
    const [editingEmailId, setEditingEmailId] = useState(null);
    const [emailDraft, setEmailDraft] = useState('');
    const [savingEmail, setSavingEmail] = useState(false);
    const [editingImieId, setEditingImieId] = useState(null);
    const [imieDraft, setImieDraft] = useState('');
    const [savingImie, setSavingImie] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
    const [archivedList, setArchivedList] = useState([]);

    const fetchArchived = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/employees`, { params: { archived_only: true } });
            setArchivedList(data);
        } catch (err) {
            console.error('Error fetching archived employees:', err);
        }
    }, []);

    useEffect(() => {
        if (viewMode === 'archived') fetchArchived();
    }, [viewMode, fetchArchived]);

    const archiveEmployee = async (emp) => {
        if (!window.confirm(`Czy na pewno chcesz przenieść pracownika ${emp.name} do archiwum?\n\nHistoria wynagrodzeń i danych pozostanie zachowana.`)) return;
        try {
            await axios.post(`${API}/employees/${emp._id}/archive`);
            onRefresh?.();
            fetchArchived();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd podczas archiwizacji');
        }
    };

    const unarchiveEmployee = async (emp) => {
        try {
            await axios.post(`${API}/employees/${emp._id}/unarchive`);
            onRefresh?.();
            fetchArchived();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd podczas przywracania');
        }
    };

    const saveEmail = async (emp) => {
        if (emailDraft && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDraft)) {
            alert('Nieprawidłowy adres email');
            return;
        }
        setSavingEmail(true);
        try {
            await axios.put(`${API}/employees/${emp._id}`, { email: emailDraft });
            setEditingEmailId(null);
            setEmailDraft('');
            onRefresh?.();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd podczas zapisywania');
        } finally {
            setSavingEmail(false);
        }
    };

    const saveImie = async (emp) => {
        setSavingImie(true);
        try {
            await axios.put(`${API}/employees/${emp._id}`, { imie_nazwisko: imieDraft || null });
            setEditingImieId(null);
            setImieDraft('');
            onRefresh?.();
        } catch (err) {
            alert(err.response?.data?.detail || 'Błąd podczas zapisywania');
        } finally {
            setSavingImie(false);
        }
    };

    const displayList = viewMode === 'active' ? employees : archivedList;

    return (
        <div className="px-1 sm:px-0" data-testid="employees-tab">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                    <h2 className="font-heading text-lg sm:text-xl font-bold">
                        {viewMode === 'active' ? 'Pracownicy' : 'Archiwum pracowników'} ({displayList.length})
                    </h2>
                    <div className="inline-flex bg-slate-100 rounded-md p-0.5" role="tablist">
                        <button
                            onClick={() => setViewMode('active')}
                            className={`px-3 py-1 text-xs font-medium rounded ${viewMode === 'active' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                            data-testid="employees-view-active"
                        >
                            Aktywni ({employees.length})
                        </button>
                        <button
                            onClick={() => setViewMode('archived')}
                            className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${viewMode === 'archived' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                            data-testid="employees-view-archived"
                        >
                            <Archive className="w-3 h-3" /> Archiwum ({archivedList.length})
                        </button>
                    </div>
                </div>
                {viewMode === 'active' && (
                    <button
                        onClick={onAddEmployee}
                        className="btn-primary flex items-center gap-2 text-sm"
                        data-testid="add-employee-btn"
                    >
                        <Plus className="w-4 h-4" /> Dodaj
                    </button>
                )}
            </div>

            {/* Desktop table (>= md) */}
            <div className="hidden md:block bg-white border border-border rounded-sm overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-muted">
                        <tr>
                            <th className="text-left py-3 px-4 field-label">Skrót</th>
                            <th className="text-left py-3 px-4 field-label">Imię i nazwisko</th>
                            <th className="text-left py-3 px-4 field-label">Data urodzenia</th>
                            <th className="text-left py-3 px-4 field-label">Email (kliknij aby edytować)</th>
                            <th className="text-right py-3 px-4 field-label">Akcje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayList.map(emp => (
                            <tr key={emp._id} className="table-row" data-testid={`employee-row-${emp._id}`}>
                                <td className="py-3 px-4 font-medium">
                                  <div className="flex items-center gap-2">
                                    {emp.name}
                                    <SalaryModeBadge config={emp.salary_config} />
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-muted-foreground">
                                    {editingImieId === emp._id ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                data-testid={`imie-input-${emp._id}`}
                                                type="text"
                                                value={imieDraft}
                                                onChange={(e) => setImieDraft(e.target.value)}
                                                placeholder="np. Jan Kowalski"
                                                autoFocus
                                                className="px-2 py-1 border rounded-sm text-sm w-60"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveImie(emp);
                                                    if (e.key === 'Escape') { setEditingImieId(null); setImieDraft(''); }
                                                }}
                                            />
                                            <button onClick={() => saveImie(emp)} disabled={savingImie} className="p-1 hover:bg-green-100 text-green-600 rounded-sm" data-testid={`imie-save-${emp._id}`}>
                                                <Check className="w-4 h-4" weight="bold" />
                                            </button>
                                            <button onClick={() => { setEditingImieId(null); setImieDraft(''); }} className="p-1 hover:bg-red-100 text-red-600 rounded-sm">
                                                <X className="w-4 h-4" weight="bold" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            data-testid={`imie-cell-${emp._id}`}
                                            onClick={() => { setEditingImieId(emp._id); setImieDraft(emp.imie_nazwisko || ''); }}
                                            className="hover:underline cursor-pointer text-left"
                                        >
                                            {emp.imie_nazwisko || <span className="italic text-amber-600">brak — kliknij aby dodać</span>}
                                        </button>
                                    )}
                                </td>
                                <td className="py-3 px-4 text-muted-foreground num text-sm">
                                    {emp.data_urodzenia || <span className="text-muted-foreground/50 italic">—</span>}
                                </td>
                                <td className="py-3 px-4 text-muted-foreground">
                                    {editingEmailId === emp._id ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                data-testid={`email-input-${emp._id}`}
                                                type="email"
                                                value={emailDraft}
                                                onChange={(e) => setEmailDraft(e.target.value)}
                                                placeholder="email@example.com"
                                                autoFocus
                                                className="px-2 py-1 border rounded-sm text-sm w-60"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEmail(emp);
                                                    if (e.key === 'Escape') { setEditingEmailId(null); setEmailDraft(''); }
                                                }}
                                            />
                                            <button onClick={() => saveEmail(emp)} disabled={savingEmail} className="p-1 hover:bg-green-100 text-green-600 rounded-sm" data-testid={`email-save-${emp._id}`}>
                                                <Check className="w-4 h-4" weight="bold" />
                                            </button>
                                            <button onClick={() => { setEditingEmailId(null); setEmailDraft(''); }} className="p-1 hover:bg-red-100 text-red-600 rounded-sm">
                                                <X className="w-4 h-4" weight="bold" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            data-testid={`email-cell-${emp._id}`}
                                            onClick={() => { setEditingEmailId(emp._id); setEmailDraft(emp.email || ''); }}
                                            className="hover:underline cursor-pointer text-left"
                                        >
                                            {emp.email || <span className="italic text-amber-600">brak — kliknij aby dodać</span>}
                                        </button>
                                    )}
                                </td>
                                <td className="py-3 px-4 text-right whitespace-nowrap">
                                    {viewMode === 'active' ? (
                                        <>
                                            <button onClick={() => onEditEmployee(emp)} className="p-2 hover:bg-muted rounded-sm" data-testid={`edit-employee-${emp._id}`} title="Edytuj">
                                                <PencilSimple className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => onResetPassword(emp._id)} className="p-2 hover:bg-muted rounded-sm" data-testid={`reset-password-${emp._id}`} title="Reset hasła">
                                                <Key className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => archiveEmployee(emp)} className="p-2 hover:bg-amber-50 text-amber-600 rounded-sm" data-testid={`archive-employee-${emp._id}`} title="Do archiwum">
                                                <Archive className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => onDeleteEmployee(emp._id)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`delete-employee-${emp._id}`} title="Usuń">
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => unarchiveEmployee(emp)} className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-sm" data-testid={`unarchive-employee-${emp._id}`} title="Przywróć">
                                                <ArrowCounterClockwise className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => onDeleteEmployee(emp._id)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`delete-employee-${emp._id}`} title="Usuń trwale">
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {employees.length === 0 && viewMode === 'active' && (
                    <div className="py-12 text-center text-muted-foreground">Brak pracowników. Dodaj pierwszego!</div>
                )}
                {archivedList.length === 0 && viewMode === 'archived' && (
                    <div className="py-12 text-center text-muted-foreground">Archiwum jest puste.</div>
                )}
            </div>

            {/* Mobile cards (< md) */}
            <div className="md:hidden space-y-2">
                {displayList.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground bg-white border border-border rounded-sm">
                        {viewMode === 'active' ? 'Brak pracowników. Dodaj pierwszego!' : 'Archiwum jest puste.'}
                    </div>
                ) : displayList.map(emp => {
                    const isExp = expandedId === emp._id;
                    return (
                        <div key={emp._id} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`employee-card-${emp._id}`}>
                            <button
                                onClick={() => setExpandedId(isExp ? null : emp._id)}
                                className="w-full flex items-center justify-between p-3 hover:bg-slate-50 active:bg-slate-100 text-left"
                                data-testid={`employee-card-toggle-${emp._id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono font-bold bg-slate-100 px-1.5 py-0.5 rounded text-xs">{emp.name}</span>
                                        {emp.imie_nazwisko && (
                                            <span className="text-sm font-semibold text-slate-800 truncate">{emp.imie_nazwisko}</span>
                                        )}
                                        <SalaryModeBadge config={emp.salary_config} />
                                    </div>
                                    {!emp.imie_nazwisko && (
                                        <div className="text-xs text-amber-600 italic mt-0.5">brak imienia — dotknij edytuj</div>
                                    )}
                                    {emp.email && (
                                        <div className="text-xs text-slate-500 truncate mt-0.5">{emp.email}</div>
                                    )}
                                </div>
                                {isExp ? <CaretUp className="w-4 h-4 text-slate-500 shrink-0 ml-2" /> : <CaretDown className="w-4 h-4 text-slate-500 shrink-0 ml-2" />}
                            </button>
                            {isExp && (
                                <div className="border-t border-slate-100 p-3 space-y-2 text-xs bg-slate-50">
                                    {emp.numer_telefonu && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                            <span className="text-slate-500">Komórka:</span>
                                            <a href={`tel:${emp.numer_telefonu.replace(/\s+/g, '')}`} className="text-foreground font-medium">{emp.numer_telefonu}</a>
                                        </div>
                                    )}
                                    {emp.numer_telefonu_zebra && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                            <span className="text-slate-500">Zebra:</span>
                                            <a href={`tel:${emp.numer_telefonu_zebra.replace(/\s+/g, '')}`} className="text-foreground font-medium">{emp.numer_telefonu_zebra}</a>
                                        </div>
                                    )}
                                    {emp.pesel && (
                                        <div className="flex items-center gap-2">
                                            <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                            <span className="text-slate-500">PESEL:</span>
                                            <span className="font-mono">{emp.pesel}</span>
                                        </div>
                                    )}
                                    {emp.data_urodzenia && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-500">Data urodzenia:</span>
                                            <span>{emp.data_urodzenia}</span>
                                        </div>
                                    )}
                                    {emp.adres && (
                                        <div className="text-slate-600">📍 {emp.adres}</div>
                                    )}
                                    {emp.email && (
                                        <div className="flex items-center gap-2">
                                            <EnvelopeSimple className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                            <a href={`mailto:${emp.email}`} className="text-foreground truncate">{emp.email}</a>
                                        </div>
                                    )}
                                    <div className="flex gap-1.5 pt-2 border-t border-slate-200">
                                        {viewMode === 'active' ? (
                                            <>
                                                <button
                                                    onClick={() => onEditEmployee(emp)}
                                                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-white hover:bg-muted border border-slate-200 text-foreground rounded text-xs font-medium"
                                                    data-testid={`edit-employee-${emp._id}`}
                                                >
                                                    <PencilSimple className="w-3.5 h-3.5" /> Edytuj
                                                </button>
                                                <button
                                                    onClick={() => onResetPassword(emp._id)}
                                                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-white hover:bg-amber-50 border border-slate-200 text-amber-700 rounded text-xs font-medium"
                                                    data-testid={`reset-password-${emp._id}`}
                                                >
                                                    <Key className="w-3.5 h-3.5" /> Hasło
                                                </button>
                                                <button
                                                    onClick={() => archiveEmployee(emp)}
                                                    className="flex items-center justify-center py-2 px-3 bg-white hover:bg-amber-50 border border-slate-200 text-amber-600 rounded text-xs font-medium"
                                                    data-testid={`archive-employee-${emp._id}`}
                                                    title="Do archiwum"
                                                >
                                                    <Archive className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => onDeleteEmployee(emp._id)}
                                                    className="flex items-center justify-center py-2 px-3 bg-white hover:bg-red-50 border border-slate-200 text-red-700 rounded text-xs font-medium"
                                                    data-testid={`delete-employee-${emp._id}`}
                                                >
                                                    <Trash className="w-3.5 h-3.5" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => unarchiveEmployee(emp)}
                                                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-white hover:bg-emerald-50 border border-slate-200 text-emerald-700 rounded text-xs font-medium"
                                                    data-testid={`unarchive-employee-${emp._id}`}
                                                >
                                                    <ArrowCounterClockwise className="w-3.5 h-3.5" /> Przywróć
                                                </button>
                                                <button
                                                    onClick={() => onDeleteEmployee(emp._id)}
                                                    className="flex items-center justify-center py-2 px-3 bg-white hover:bg-red-50 border border-slate-200 text-red-700 rounded text-xs font-medium"
                                                    data-testid={`delete-employee-${emp._id}`}
                                                >
                                                    <Trash className="w-3.5 h-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
